import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import NotificationBellButton from '../components/NotificationBellButton';
import { BellIcon } from '../components/BellIcon';
import { GuestBottomNav } from '../components/GuestBottomNav';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { acceptInvite, getInvite, requestInviteAccess } from '../utils/inviteApi';
import { consumePendingInviteId, resolveInviteEntry, setActiveGuestInviteId, storePendingInviteId } from '../utils/deepLinkHandler';
import { formatEuropeanDate, toInputDateValue } from '../utils/dateFormat';
import InviteAppGate from '../components/InviteAppGate';
import { trackInviteViewed, trackInviteAccepted, trackQrUnlocked } from '../utils/airbridge';
import { persistAuthSession } from '../utils/authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WEEK_DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const fontFamily = "'Inria Serif', serif";

function PremiumDatePicker({ value, onChange, onClose, maxDate }) {
  const initialDate = value ? new Date(value) : new Date();
  const [viewDate, setViewDate] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));

  const selectedDate = value ? new Date(value) : null;
  const maxAllowedDate = maxDate ? new Date(maxDate) : null;
  if (maxAllowedDate) maxAllowedDate.setHours(0, 0, 0, 0);
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = [];
  for (let i = firstDay - 1; i >= 0; i -= 1) {
    days.push({ key: `prev-${i}`, label: prevMonthDays - i, currentMonth: false });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, month, day);
    days.push({ key: `current-${day}`, label: day, currentMonth: true, date });
  }
  while (days.length % 7 !== 0) {
    days.push({ key: `next-${days.length}`, label: days.length % 7, currentMonth: false });
  }

  const isSelected = (date) => selectedDate && date && date.toDateString() === selectedDate.toDateString();
  const isToday = (date) => date && date.toDateString() === today.toDateString();

  return (
    <div className="premium-card" style={{ marginTop: 10, borderRadius: 26, padding: '18px 18px 14px', position: 'relative', zIndex: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <button onClick={() => setViewDate(new Date(year, month - 1, 1))} data-testid="guest-calendar-prev-button" className="premium-card-compact premium-card-nested" style={{ width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronLeft size={18} color="var(--text)" /></button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 24, fontWeight: 700 }}>{viewDate.toLocaleString('en-GB', { month: 'long', year: 'numeric' })}</span>
          <ChevronDown size={18} color="var(--text)" />
        </div>
        <button onClick={() => setViewDate(new Date(year, month + 1, 1))} data-testid="guest-calendar-next-button" className="premium-card-compact premium-card-nested" style={{ width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronRight size={18} color="var(--text)" /></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, marginBottom: 10 }}>
        {WEEK_DAYS.map((label) => (
          <div key={label} style={{ textAlign: 'center', fontSize: 14, color: 'var(--text-2)', fontWeight: 700 }}>{label}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
        {days.map((day) => {
          const active = isSelected(day.date);
          const disabled = !day.currentMonth || !day.date || day.date < today || (maxAllowedDate && day.date > maxAllowedDate);
          return (
            <button
              key={day.key}
              disabled={disabled}
              onClick={() => {
                if (disabled || !day.date) return;
                onChange(toInputDateValue(day.date));
                onClose();
              }}
              data-testid={day.currentMonth && day.date ? `guest-calendar-day-${toInputDateValue(day.date)}` : undefined}
              style={{
        border: active ? '2px solid var(--accent-purple)' : isToday(day.date) ? '2px solid #E53935' : '1px solid var(--card-border)',
                background: active ? 'var(--accent-surface)' : day.currentMonth ? 'var(--surface)' : 'var(--app-bg)',
                color: disabled ? 'var(--text-3)' : 'var(--text)',
                borderRadius: 14,
                aspectRatio: '1',
                cursor: disabled ? 'default' : 'pointer',
                fontFamily: "'Inria Serif', serif",
                fontSize: 20,
                fontWeight: active ? 700 : 400,
              }}
            >
              {day.label}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
        <button onClick={() => onChange('')} data-testid="guest-calendar-clear-button" style={{ background: 'none', border: 'none', color: 'var(--accent-purple)', fontFamily: "'Inria Serif', serif", fontSize: 17, cursor: 'pointer' }}>Clear</button>
        <button onClick={() => { onChange(toInputDateValue(new Date())); onClose(); }} disabled={maxAllowedDate && today > maxAllowedDate} data-testid="guest-calendar-today-button" style={{ background: 'none', border: 'none', color: maxAllowedDate && today > maxAllowedDate ? 'var(--text-3)' : 'var(--accent-purple)', fontFamily: "'Inria Serif', serif", fontSize: 17, cursor: maxAllowedDate && today > maxAllowedDate ? 'default' : 'pointer' }}>Today</button>
      </div>
    </div>
  );
}

export default function GuestInviteEntryPage() {
  const { inviteId: routeInviteId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));
  const actualInviteId = resolveInviteEntry({ routeInviteId });
  const [invite, setInvite] = useState(null);
  const [checkInDate, setCheckInDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [fetchErrorStatus, setFetchErrorStatus] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [roleSwitching, setRoleSwitching] = useState(false);
  const checkInInputRef = useRef(null);
  const [showCalendar, setShowCalendar] = useState(false);

  // AUTO-SWITCH TO GUEST ROLE
  // -------------------------------------------------------------------
  // A user can hold multiple roles (guest + host + venue_owner + admin)
  // and the currently active `role` in localStorage is whichever one they
  // last switched into. If they open an invite link while their active
  // role is anything other than `guest`, the Accept button would be
  // hidden and they'd see the logged-out "Log in / Create account" state
  // even though they're fully authenticated — bug reported 2026-02.
  //
  // Fix: on mount, detect (token + non-guest role) and silently call
  // POST /api/auth/switch-role → guest so the invite renders correctly.
  // If the switch fails (rare), we fall through to the existing UI.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    if (!stored || stored.role === 'guest') return;
    let cancelled = false;
    setRoleSwitching(true);
    axios
      .post(
        `${API}/auth/switch-role`,
        { target_role: 'guest' },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then(async (res) => {
        if (cancelled) return;
        await persistAuthSession(res.data.access_token, res.data.user);
        setUser(res.data.user);
      })
      .catch(() => {
        // Non-fatal: keep current session; the existing "Log in / Create
        // account" fallback still renders and clicking either button
        // routes through the normal auth flow.
      })
      .finally(() => {
        if (!cancelled) setRoleSwitching(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runFetchInvite = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvite(actualInviteId);
      setInvite(res);
      setFetchFailed(false);
      setFetchErrorStatus(null);
      // Airbridge: record that this invite landing page was viewed so the
      // host→guest funnel (click → view → accept → QR unlock) is complete
      // in the dashboard.
      trackInviteViewed({
        inviteId: actualInviteId,
        inviteType: 'host_to_guest',
        extra: {
          host_id: res?.host_id || null,
          listing_id: res?.listing_id || null,
          status: res?.status || null,
        },
      });
    } catch (err) {
      // Surface the HTTP status so the retry card can distinguish "invite
      // truly does not exist" (404) from a transient network failure (0 /
      // 5xx) — the latter gets a retry CTA, the former gets the classic
      // "ask your host to send a fresh link" copy.
      const status = err?.response?.status ?? 0;
      setFetchErrorStatus(status);
      setFetchFailed(true);
      if (status !== 404) {
        toast.error('Could not load invite');
      }
    } finally {
      setLoading(false);
    }
  }, [actualInviteId]);

  useEffect(() => {
    if (actualInviteId) {
      runFetchInvite();
      if (!user) {
        storePendingInviteId(actualInviteId);
      } else {
        consumePendingInviteId();
      }
    } else {
      setLoading(false);
      setFetchFailed(true);
    }
    // `user` intentionally omitted — it's read once from localStorage above.
  }, [actualInviteId, runFetchInvite]);

  useEffect(() => {
    const handleOutside = (event) => {
      if (checkInInputRef.current && !checkInInputRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleRequest = async () => {
    if (!checkInDate) {
      toast.error('Please enter your check-in date');
      return;
    }
    try {
      const updated = await requestInviteAccess(actualInviteId, { check_in_date: checkInDate });
      setActiveGuestInviteId(actualInviteId);
      navigate(`/invite/${actualInviteId}/waiting`, { state: { conversationId: updated.conversation_id } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request access');
    }
  };

  const handleAcceptInvite = async () => {
    setAccepting(true);
    try {
      await acceptInvite(actualInviteId);
      setActiveGuestInviteId(actualInviteId);
      // Airbridge: fire both the acceptance and the QR-unlock events because
      // accepting an invite immediately unlocks the guest's QR pass.
      trackInviteAccepted({
        inviteId: actualInviteId,
        inviteType: 'host_to_guest',
        extra: { host_id: invite?.host_id || null, listing_id: invite?.listing_id || null },
      });
      trackQrUnlocked({ inviteId: actualInviteId });
      // Note: the celebratory sound + success haptic fire on the waiting
      // page the instant the green ✓ badge animates in (see
      // GuestInviteWaitingPage) so audio & visual stay perfectly in sync.
      navigate(`/invite/${actualInviteId}/waiting`, { state: { autoLaunchQr: true } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  const canContinue = user && user.role === 'guest';
  const canRequest = canContinue && (invite?.status || 'created') === 'created';
  // Direct-accept: the host provided dates upfront (manual invite with dates,
  // iCal-imported reservation, or any invite the backend has set both dates
  // on). The old "guest enters check-in date manually" flow only applies to
  // invites created by the host WITHOUT dates (rare edge case).
  const isDirectAcceptInvite = Boolean(invite?.check_in_date && invite?.checkout_date);
  const inviteOwnedByCurrentGuest = Boolean(user?.id && invite?.guest_id && user.id === invite.guest_id);
  const inviteAlreadyUsedByAnotherGuest = Boolean(invite?.guest_id) && !inviteOwnedByCurrentGuest;

  const renderDirectAcceptInvite = () => {
    if (!invite) return null;

    const canAcceptNow = canContinue && invite.status === 'confirmed_by_host' && !inviteAlreadyUsedByAnotherGuest;
    const canOpenQr = canContinue && invite.status === 'approved' && inviteOwnedByCurrentGuest;
    const isUnavailable = ['declined_by_host', 'cancelled_by_ical'].includes(invite.status) || inviteAlreadyUsedByAnotherGuest;
    const isWaitingForHost = canContinue && invite.status === 'pending_host_approval' && inviteOwnedByCurrentGuest;
    const listingImage = invite.listing_image || '/allure-logo-mono.png';
    const statusPill = invite.status === 'approved'
      ? 'QR ready'
      : isWaitingForHost
        ? 'Awaiting host'
        : canAcceptNow
          ? 'Direct accept'
          : isUnavailable
            ? 'Unavailable'
            : 'Stay details';
    const bodyCopy = canAcceptNow
      ? 'Your dates are already pre-filled. Accept once to unlock your Allure QR for this stay.'
      : canOpenQr
        ? 'Your stay is already active. Open your QR whenever you are ready to head out.'
        : isWaitingForHost
          ? 'Waiting for your host to approve your access. We\u2019ll unlock your QR the moment they confirm.'
          : isUnavailable
            ? inviteAlreadyUsedByAnotherGuest
              ? 'This invite has already been claimed by another guest account.'
              : 'This reservation is no longer available for activation.'
            : 'Review the stay details below before continuing.';

    return (
      <motion.div
        className="premium-card"
        data-testid="guest-direct-invite-card"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.36, ease: 'easeOut' }}
        style={{ width: '100%', marginTop: 24, borderRadius: 28, padding: '18px 18px 22px', overflow: 'hidden' }}
      >
        <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 10', borderRadius: 24, overflow: 'hidden', background: 'var(--surface-2)' }}>
          <img src={listingImage} alt={invite.listing_name} data-testid="guest-direct-invite-image" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.02) 15%, rgba(0,0,0,0.34) 100%)' }} />
          <div data-testid="guest-direct-invite-status-pill" style={{ position: 'absolute', top: 14, left: 14, borderRadius: 9999, background: 'var(--nav-surface)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', border: '1px solid var(--card-border)', boxShadow: 'var(--nav-shadow)', padding: '8px 14px', fontFamily, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{statusPill}</div>
        </div>

        <p data-testid="guest-direct-invite-listing-name" style={{ fontFamily, fontSize: 27, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '18px 0 0', lineHeight: 1.05 }}>
          {invite.listing_name}
        </p>
        <p data-testid="guest-direct-invite-host-name" style={{ fontFamily, fontSize: 17, fontWeight: 700, color: 'var(--text-2)', textAlign: 'center', margin: '8px 0 0' }}>
          Invited by {invite.host_name}
        </p>
        <p data-testid="guest-direct-invite-body-copy" style={{ fontFamily, fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '14px auto 0', lineHeight: 1.28, maxWidth: 300 }}>
          {bodyCopy}
        </p>

        <div className="premium-card-compact premium-card-nested" data-testid="guest-direct-invite-dates" style={{ marginTop: 18, textAlign: 'center', borderRadius: 22, padding: '14px 16px' }}>
          <p style={{ fontFamily, fontSize: 13, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Stay window</p>
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
              <p style={{ fontFamily, fontSize: 12, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Check-in</p>
              <p data-testid="guest-direct-invite-checkin-date" style={{ fontFamily, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{formatEuropeanDate(invite.check_in_date)}</p>
            </div>
            <div style={{ height: 1, background: 'var(--card-border)' }} />
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
              <p style={{ fontFamily, fontSize: 12, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Checkout</p>
              <p data-testid="guest-direct-invite-checkout-date" style={{ fontFamily, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{formatEuropeanDate(invite.checkout_date)}</p>
            </div>
          </div>
        </div>

        {canAcceptNow ? (
          <motion.button whileTap={{ scale: 0.985 }} onClick={handleAcceptInvite} disabled={accepting} data-testid="guest-accept-invite-button" style={{ margin: '22px auto 0', display: 'block', width: '100%', maxWidth: 280, padding: '14px 0', borderRadius: 9999, border: 'none', background: 'var(--accent-purple)', color: '#FFFFFF', fontFamily, fontSize: 21, fontWeight: 700, cursor: 'pointer', boxShadow: 'var(--premium-shadow-main)', opacity: accepting ? 0.7 : 1 }}>
            {accepting ? 'Preparing...' : 'Accept'}
          </motion.button>
        ) : canOpenQr ? (
          <motion.button whileTap={{ scale: 0.985 }} onClick={() => navigate('/guest/qr')} data-testid="guest-open-qr-button" className="premium-pill-button" style={{ margin: '22px auto 0', display: 'block', width: '100%', maxWidth: 280, padding: '14px 0', fontFamily, fontSize: 21, fontWeight: 700, cursor: 'pointer' }}>
            Open QR
          </motion.button>
        ) : isWaitingForHost ? (
          <div data-testid="guest-direct-invite-waiting" className="premium-card-compact premium-card-nested" style={{ marginTop: 20, borderRadius: 20, padding: '16px 18px' }}>
            <p style={{ fontFamily, fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: 0, lineHeight: 1.28 }}>
              Awaiting host approval — you&rsquo;ll get a push notification once it&rsquo;s ready.
            </p>
          </div>
        ) : isUnavailable ? (
          <div data-testid="guest-direct-invite-unavailable" className="premium-card-compact premium-card-nested" style={{ marginTop: 20, borderRadius: 20, padding: '16px 18px' }}>
            <p style={{ fontFamily, fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: 0, lineHeight: 1.28 }}>
              {inviteAlreadyUsedByAnotherGuest ? 'This invite link has already been used.' : 'This reservation is no longer available.'}
            </p>
          </div>
        ) : !canContinue ? (
          roleSwitching ? (
            <div data-testid="guest-invite-role-switch-loading" className="premium-card-compact premium-card-nested" style={{ marginTop: 20, borderRadius: 20, padding: '16px 18px' }}>
              <p style={{ fontFamily, fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: 0, lineHeight: 1.28 }}>
                Preparing your access…
              </p>
            </div>
          ) : (
            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={() => navigate('/login')} data-testid="guest-invite-login-button" className="premium-pill-button" style={{ width: '100%', padding: '14px 0', fontFamily, fontSize: 20, fontWeight: 700, cursor: 'pointer' }}>Log in to continue</button>
              <button onClick={() => navigate('/register')} data-testid="guest-invite-register-button" className="premium-pill-button" style={{ width: '100%', padding: '14px 0', fontFamily, fontSize: 19, fontWeight: 700, cursor: 'pointer' }}>Create account</button>
            </div>
          )
        ) : null}

        <button onClick={() => navigate('/guest/qr')} data-testid="guest-invite-change-link-button" style={{ marginTop: 14, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily, fontSize: 18, color: 'var(--text)', textDecoration: 'underline' }}>← Use another invitation link</button>
      </motion.div>
    );
  };

  const hostNameForGate = invite?.host_name ? `Invited by ${invite.host_name}` : '';
  const listingForGate = invite?.listing_name ? `Stay: ${invite.listing_name}` : '';
  const gateSubLabel = [hostNameForGate, listingForGate].filter(Boolean).join(' · ');

  return (
    <InviteAppGate
      invitedToLabel="You're invited to Allure"
      invitedSubLabel={gateSubLabel}
      inviteId={actualInviteId}
    >
    <div className="app-shell" data-testid="guest-invite-entry-page" style={{ backgroundColor: 'var(--surface)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'flex-end' }}>
        {canContinue ? (
          <NotificationBellButton buttonTestId="guest-invite-bell-button" popupTestId="guest-invite-notification-popup" />
        ) : (
          <div data-testid="guest-invite-bell-placeholder" style={{ padding: 4 }}><BellIcon /></div>
        )}
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
        <RouteTransitionLogo width={140} height={125} dataTestId="guest-invite-entry-shared-logo" />
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.34, delay: 0.04, ease: 'easeOut' }} data-testid="guest-invite-entry-eyebrow" style={{ fontFamily, fontSize: 15, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-2)', textAlign: 'center', margin: '10px 0 0' }}>
          Guest access
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.34, delay: 0.08, ease: 'easeOut' }} data-testid="guest-invite-entry-title" style={{ fontFamily, fontSize: 27, fontWeight: 700, color: 'var(--text)', textAlign: 'center', marginTop: 16, lineHeight: 1.08, maxWidth: 320 }}>
          {fetchFailed ? 'Invite unavailable' : isDirectAcceptInvite ? 'Your Allure invite' : 'Confirm your stay'}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.34, delay: 0.12, ease: 'easeOut' }} data-testid="guest-invite-entry-subtitle" style={{ fontFamily, fontSize: 19, fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.28, marginTop: 12, maxWidth: 328 }}>
          {fetchFailed ? 'This invite link is no longer valid, has expired, or was mistyped. Ask your host to send you a fresh link.' : isDirectAcceptInvite ? 'Review your stay details and accept to unlock your QR code.' : 'Add your check-in date so the host can confirm your Allure access.'}
        </motion.p>

        {loading ? <p data-testid="guest-invite-loading-text" style={{ fontFamily, fontSize: 18, color: 'var(--text-2)', marginTop: 22 }}>Loading invitation...</p> : null}

        {fetchFailed && !loading ? (
          <div data-testid="guest-invite-error-card" className="premium-card" style={{ width: '100%', marginTop: 24, borderRadius: 28, padding: '22px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            {actualInviteId ? (
              <div className="premium-card-compact premium-card-nested" data-testid="guest-invite-error-code" style={{ borderRadius: 14, padding: '10px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <p style={{ fontFamily, fontSize: 13, color: 'var(--text-3)', margin: 0, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Invite code we tried</p>
                <p style={{ fontFamily: "'JetBrains Mono', 'Menlo', monospace", fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0, letterSpacing: '0.12em' }}>{actualInviteId}</p>
              </div>
            ) : null}
            <p style={{ fontFamily, fontSize: 17, color: 'var(--text-2)', textAlign: 'center', margin: 0, lineHeight: 1.35 }}>
              {fetchErrorStatus === 404
                ? 'If you already have an Allure account, head to your QR page — any active invite will surface there automatically.'
                : 'We couldn\u2019t reach the invite service. Check your connection and try again.'}
            </p>
            {fetchErrorStatus !== 404 && actualInviteId ? (
              <button onClick={runFetchInvite} data-testid="guest-invite-error-retry-button" className="premium-pill-button" style={{ width: '100%', maxWidth: 280, padding: '14px 0', fontFamily, fontSize: 20, fontWeight: 700, cursor: 'pointer' }}>
                Try again
              </button>
            ) : null}
            <button onClick={() => navigate(user ? '/guest/qr' : '/login')} data-testid="guest-invite-error-continue-button" className="premium-pill-button" style={{ width: '100%', maxWidth: 280, padding: '14px 0', fontFamily, fontSize: 20, fontWeight: 700, cursor: 'pointer' }}>
              {user ? 'Go to my QR' : 'Log in to Allure'}
            </button>
          </div>
        ) : null}

        {invite && isDirectAcceptInvite ? renderDirectAcceptInvite() : null}

        {invite && !isDirectAcceptInvite ? (
          <motion.div className="premium-card" data-testid="guest-manual-invite-card" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.36, ease: 'easeOut' }} style={{ width: '100%', marginTop: 24, borderRadius: 28, padding: '18px 18px 22px' }}>
            {invite.listing_image ? (
              <div style={{ width: '100%', aspectRatio: '16 / 10', borderRadius: 24, overflow: 'hidden', background: 'var(--surface-2)', marginBottom: 18 }}>
                <img src={invite.listing_image} alt={invite.listing_name} data-testid="guest-manual-invite-image" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ) : null}
            <p data-testid="guest-invite-listing-name" style={{ fontFamily, fontSize: 28, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: 0, lineHeight: 1.08 }}>{invite.listing_name}</p>
            <p data-testid="guest-manual-invite-description" style={{ fontFamily, fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '12px auto 0', lineHeight: 1.28, maxWidth: 300 }}>
              Share your arrival date so your host can match it to the correct stay.
            </p>
            <p style={{ fontFamily, fontSize: 17, color: 'var(--text)', textAlign: 'center', margin: '18px 0 0' }}>Check-in date</p>
            <div ref={checkInInputRef} style={{ position: 'relative', marginTop: 8 }}>
              <button onClick={() => setShowCalendar((prev) => !prev)} data-testid="guest-invite-checkin-input" className="premium-input-shell" style={{ width: '100%', borderRadius: 18, border: '1px solid var(--card-border)', padding: '12px 14px', textAlign: 'center', fontFamily, fontSize: 20, outline: 'none', boxSizing: 'border-box', cursor: 'pointer' }}>{checkInDate ? formatEuropeanDate(checkInDate) : 'Select dates'}</button>
              {showCalendar ? <PremiumDatePicker value={checkInDate} onChange={setCheckInDate} onClose={() => setShowCalendar(false)} maxDate={invite.checkout_date} /> : null}
            </div>
            <div className="premium-card-compact premium-card-nested" data-testid="guest-invite-checkout-card" style={{ marginTop: 16, borderRadius: 20, padding: '14px 16px', textAlign: 'center' }}>
              <p style={{ fontFamily, fontSize: 15, color: 'var(--text-2)', margin: 0 }}>Check-out date</p>
              <p data-testid="guest-invite-checkout-value" style={{ fontFamily, fontSize: 24, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '6px 0 0', lineHeight: 1.1 }}>{formatEuropeanDate(invite.checkout_date)}</p>
            </div>

            {canRequest ? (
              <motion.button whileTap={{ scale: 0.985 }} onClick={handleRequest} data-testid="guest-request-access-button" className="premium-pill-button" style={{ margin: '24px auto 0', display: 'block', width: '100%', maxWidth: 280, padding: '14px 0', fontFamily, fontSize: 21, fontWeight: 700, cursor: 'pointer' }}>Request Access</motion.button>
            ) : canContinue ? (
              <motion.button whileTap={{ scale: 0.985 }} onClick={() => navigate(`/invite/${actualInviteId}/waiting`, { state: { conversationId: invite?.conversation_id } })} data-testid="guest-view-request-status-button" className="premium-pill-button" style={{ margin: '24px auto 0', display: 'block', width: '100%', maxWidth: 280, padding: '14px 0', fontFamily, fontSize: 21, fontWeight: 700, cursor: 'pointer' }}>{invite?.status === 'approved' ? 'View approval' : 'View request status'}</motion.button>
            ) : roleSwitching ? (
              <div data-testid="guest-invite-manual-role-switch-loading" className="premium-card-compact premium-card-nested" style={{ marginTop: 24, borderRadius: 20, padding: '16px 18px' }}>
                <p style={{ fontFamily, fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: 0, lineHeight: 1.28 }}>
                  Preparing your access…
                </p>
              </div>
            ) : (
              <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={() => navigate('/login')} data-testid="guest-invite-login-button" className="premium-pill-button" style={{ width: '100%', padding: '14px 0', fontFamily, fontSize: 20, fontWeight: 700, cursor: 'pointer' }}>Log in to continue</button>
                <button onClick={() => navigate('/register')} data-testid="guest-invite-register-button" className="premium-pill-button" style={{ width: '100%', padding: '14px 0', fontFamily, fontSize: 19, fontWeight: 700, cursor: 'pointer' }}>Create account</button>
              </div>
            )}

            <button onClick={() => navigate('/guest/qr')} data-testid="guest-invite-change-link-button" style={{ marginTop: 14, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily, fontSize: 18, color: 'var(--text)', textDecoration: 'underline' }}>← Use another invitation link</button>
          </motion.div>
        ) : null}

        <div data-testid="guest-invite-security-card" className="premium-card-compact premium-card-nested" style={{ width: '100%', marginTop: 18, borderRadius: 22, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 28 }}>🔒</span>
          <p style={{ fontFamily, fontSize: 18, color: 'var(--text-2)', margin: 0, lineHeight: 1.25 }}>Your information is used only to verify your stay</p>
        </div>
      </div>

      {canContinue ? <GuestBottomNav /> : null}
    </div>
    </InviteAppGate>
  );
}